import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCustomers } from '../../context/CustomerContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useRoutePath } from '../../utils/getRoutePath';
import { useGoogleAutocomplete } from "../../hooks/useGoogleAutocomplete";

// Import tab components
import AccountInfoTab from './tabs/AccountInfoTab';
import FinancialDataTab from './tabs/FinancialDataTab';
import EquipmentTab from './tabs/EquipmentTab';
import DocumentsTab from './tabs/DocumentsTab';
import HistoryTab from './tabs/HistoryTab';
import LogsTab from './tabs/LogsTab';

import { useLoading } from '../../context/LoadingContext';
import { SaveIcon, ArrowBigLeft, ArrowBigRight } from 'lucide-react';
import { usePaymentTerms } from '../../context/PaymentTermContext';
import { usePaymentMethods } from '../../context/PaymentMethodContext';
import { useEquipmentManufacturers } from '../../context/EquipmentManufacturerContext';
import { useEquipmentTypes } from '../../context/EquipmentTypeContext';
 
const schema = yup.object().shape({
  name: yup.string().required('Customer name is required'),
 // parent_account_id: yup.string().required('Parent account is required'),
  account_number: yup.string(),
  primary_contact: yup.object().shape({
    prefix_id: yup.string(),
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    phone_numbers: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Phone type is required'),
        number: yup.string()
          .required('Phone number is required')
          .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in format (xxx) xxx-xxxx'),
        ext: yup.string() 
      })
    ).min(1, 'At least one phone number is required'),
    emails: yup.array().of(
      yup.object().shape({
        type: yup.string().required('Email type is required'),
        address: yup.string()
          .email('Invalid email format')
          .required('Email address is required')
      })
    ).min(1, 'At least one email is required')
  }),
  service_locations: yup.array().of(
    yup.object().shape({
      location_nickname: yup.string(),
      street_address: yup.string().required('Street address is required'),
      city: yup.string().required('City is required'),
      state: yup.string().required('State/Province is required'),
      zipcode: yup.string().required('ZIP/Postal code is required')
    })
  ).min(1, 'At least one service location is required')
});

const CustomerForm = () => {
  const getRoutePath = useRoutePath();
  const { addCustomer, updateCustomer, getCustomerById ,getCustomerAdditionalData,getCustomers} = useCustomers();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account-info');
  const [documents, setDocuments] = useState([]);
  const [getCustomerTags, setGetCustomerTags] = useState([]);
  const [getReferralSources, setReferralSources] = useState([]);
  const [getIndustries, setGetIndustries] = useState([]);
  const [getAssignedContracts, setAssignedContracts] = useState([]);
  const [getTaxItems, setGetTaxItems] = useState([]);
  const [getCurrencies, setGetCurrencies] = useState([]);
  const [getAssignedAgents, setAssignedAgents] = useState([]);
  const [getPrefixes, setPrefixes] = useState([]);
  const [getFrequencies, setFrequencies] = useState([]);
  const { getPaymentTerms } = usePaymentTerms();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const { getPaymentMethods } = usePaymentMethods();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const paymentMethodRef = useRef(false);
  const paymentTermRef = useRef(false);
  const { getEquipmentManufacturers } = useEquipmentManufacturers();
  const { getEquipmentTypes } = useEquipmentTypes();
  const [equipmentManufacturers, setEquipmentManufacturers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const equipmentManufacturerRef = useRef(false);
  const equipmentTypeRef = useRef(false);

  useEffect(() => {
    const fetchEquipmentManufacturers = async () => {
      const response = await getEquipmentManufacturers(1, { all: true });
      setEquipmentManufacturers(response?.data || []);
    }
    if(equipmentManufacturerRef.current === false){
      fetchEquipmentManufacturers();
      equipmentManufacturerRef.current = true;
    }
  }, [getEquipmentManufacturers]);
  
  useEffect(() => {
    const fetchEquipmentTypes = async () => {
      const response = await getEquipmentTypes(1, { all: true });
      setEquipmentTypes(response?.data || []);
    }
    if(equipmentTypeRef.current === false){
      fetchEquipmentTypes();
      equipmentTypeRef.current = true;
    }
  }, [getEquipmentTypes]);
 
  useEffect(() => {
   
      const fetPaymentTerms = async () => {
        const response = await getPaymentTerms(1, { all: true, active: true });
        
        setPaymentTerms(response?.data || []);
      }
      if(!paymentTermRef.current){
      fetPaymentTerms();
      paymentTermRef.current = true;
      }
    
  }, [getPaymentTerms]);

  useEffect(() => {

    const fetPaymentMethods = async () => {
      const response = await getPaymentMethods(1, { all: true, active: true });
      setPaymentMethods(response?.data || []);
    }
    if(!paymentMethodRef.current){
      fetPaymentMethods();
      paymentMethodRef.current = true;
    }
  }, [getPaymentMethods]);


  const [history] = useState({
    last12Months: 0,
    lifetimeRevenue: 0,
    totalJobs: 0,
    aov: 0,
    currentBalance: 0
  });
  const [logs] = useState({
    emailMessages: [],
    textMessages: [],
    callLogs: [],
    voiceReminders: []
  });
  const hasFetched = useRef(false);
  const { showLoading, hideLoading } = useLoading();
  const [isLoading, setIsLoading] = useState(false);
  const [getEditData, setGetEditData] = useState(null);
   
  useEffect(() => {
    const fetchCustomerAdditionalData = async () => {
      const response = await getCustomerAdditionalData(); // ✅ calls the useCallback version
      
     if(response?.customerTags){
      setGetCustomerTags(response?.customerTags?.data);
     }

     if(response?.referralSources){
     
      setReferralSources(response?.referralSources);
     }
     if(response?.industries){
      setGetIndustries(response?.industries);
     }
     if(response?.assignedContracts){
      setAssignedContracts(response?.assignedContracts);
     }
     if(response?.taxItems){
      setGetTaxItems(response?.taxItems);
     }
     if(response?.currencies){
      setGetCurrencies(response?.currencies);
     }
     if(response?.assignedAgents){
      setAssignedAgents(response?.assignedAgents?.data);
     }
     if(response?.primaryContractPrefixes){
      setPrefixes(response?.primaryContractPrefixes);
     }

     if(response?.sAgreementFrequencies){
      setFrequencies(response?.sAgreementFrequencies)
     }
    };
    fetchCustomerAdditionalData();
  }, [getCustomerAdditionalData]);

  // Tab configuration
  const tabs = [
    { id: 'account-info', label: 'Account Info' },
    { id: 'financial-data', label: 'Financial Data' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'documents', label: 'Documents' },
    { id: 'history', label: 'History' },
    { id: 'logs', label: 'Logs' }
  ];
  
  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
     
    return {
      name: data.name || '',
      parent_account_id: data.parent_account_id || '',
      account_number: data.account_number || '',
      is_vip_account: data.is_vip_account || false,
      has_service_agreement: data.has_service_agreement || false,
      service_agreements: data.service_agreements || [],
      active: data.active ?? true,
      primary_contact: data.primary_contact || {
        prefix_id: '',
        first_name: '',
        last_name: '',
        suffix: '',
        phone_numbers: [{ type: 'mobile', number: '', ext: '' }],
        department: '',
        job_title: '',
        emails: [{ type: 'Personal', address: '' }],
        is_billing_contact: false,
        is_booking_contact: false,
        birthday: '',
        anniversary: ''
      },
      service_locations: data.service_locations || [{
        location_nickname: '',
        is_primary_location: true,
        is_billing_address: true,
        street_address: '',
        apt: '',
        city: '',
        state: '',
        zipcode: '',
        contact: '',
        active: true,
        is_gated_property: false,
        gate_access_instructions: ''
      }],
      private_notes: data.private_notes || '',
      order_notes: data.order_notes || '',
      customer_tag_ids: data.customer_tag_ids || '',
      referral_source_id: data.referral_source_id || '',
      industry_id: data.industry_id || '',
      text: data.text || '',
      assigned_contract: data.assigned_contract || '',
      assigned_contract_id: data.assigned_contract_id || '',
      is_taxable: data.is_taxable || false,
      tax_item_id: data.tax_item_id || '',
      default_currency_id: data.default_currency_id || '',
      tax_id: data.tax_id || '',
      assigned_agents: data.assigned_agents || [{ agent_rep_id: '', commission_type: '', commission: '' }],
      created_by: user?.id || '',
      created_at: data.createdAt || new Date().toISOString(),
      // Financial Data fields
      default_payment_method: data.default_payment_method || '',
      payment_terms: data.payment_terms || '',
      discount: data.discount || '',
      discount_type: data.discount_type || '%',
      credit_rating: data.credit_rating || '',
      labor_charge_type: data.labor_charge_type || '',
      amount_per_service_call: data.amount_per_service_call || '',
      bill_for_drive_time: data.bill_for_drive_time || false,
      documents: data.documents || []
    };
  }, [user?.id]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });
  
  useGoogleAutocomplete({ setValue });
  
  useEffect(() => {
    if(!id){
      reset(getDefaultValues()); 
      setDocuments([])
    }
  }, [id,reset,getDefaultValues]);
   
 
  const fetchCustomerData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getCustomerById(id);
        if (response?.data) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
        toast.error('Error fetching customer data');
      } finally {
        hideLoading();
      }
    }
      
  }, [id, getCustomerById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  const handleTabChange = (tabId) => {
   
    // Get required fields for current tab
    const currentTabFields = getTabFields(activeTab);
   
    // Check all required fields
    const emptyFields = currentTabFields.filter(field => {
      
      let value = watch(field);
     
      if(field === 'primary_contact.emails'){
        value.forEach((email, index) => {  
          if(email.address === ''){
            setError(`primary_contact.emails[${index}].address`, {
              type: 'required',
              message: 'Email address is required'
            }); 
            return value = [];
          }else if(email.address !== ''){
            if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.address)){
              setError(`primary_contact.emails[${index}].address`, {
                type: 'invalid',
                message: 'Invalid email address'
              }); 
              return value = [];
            }
          }
          
          if(email.type === ''){
            setError(`primary_contact.emails[${index}].type`, {
              type: 'required',
              message: 'Email type is required'
            }); 
            return value = [];
          } 
        });
       
      }
      if(field === 'primary_contact.phone_numbers'){
        value.forEach((phone, index) => {
          
          if(phone.number === ''){
            setError(`primary_contact.phone_numbers[${index}].number`, {
              type: 'required',
              message: 'Phone number is required'
            }); 
            return value = [];
          }else if(phone.number !== ''){
            if(!phone.number.match(/^\(\d{3}\) \d{3}-\d{4}$/)){
              setError(`primary_contact.phone_numbers[${index}].number`, {
                type: 'invalid',
                message: 'Phone number must be in format (xxx) xxx-xxxx'
              }); 
              return value = [];
            }
          }
          if(phone.type === ''){
            setError(`primary_contact.phone_numbers[${index}].type`, {
              type: 'required',
              message: 'Phone type is required'
            }); 
            return value = [];
          }
          if(phone.ext === ''){ 
           
            // setError(`primary_contact.phone_numbers[${index}].ext`, {
            //   type: 'required',
            //   message: 'Phone extension is required'
            // }); 
            // return value = [];
          }
        });
         
      }
      
      if(field === 'service_locations'){
        value.forEach((location, index) => {
          
          if(location.street_address === ''){
            setError(`service_locations[${index}].street_address`, {
              type: 'required',
              message: 'Street address is required'
            });
            value = [];
          }
          if(location.city === ''){
            setError(`service_locations[${index}].city`, {
              type: 'required',
              message: 'City is required'
            });
            value = [];
          }
          if(location.state === ''){
            setError(`service_locations[${index}].state`, {
              type: 'required',
              message: 'State is required'
            });
            value = [];
          }
          if(location.zipcode === ''){
            setError(`service_locations[${index}].zipcode`, {
              type: 'required',
              message: 'ZIP/Postal code is required'
            });
            value = [];
          }
        });
      }
      // Handle falsy values like "", null, undefined, empty arrays
      if (Array.isArray(value)) {
        return value.length === 0;
      }
    
      return value === undefined || value === null || value === '';
    });
     
    if (emptyFields.length > 0) {
      // Set errors for all empty fields
    
      emptyFields.forEach(field => {
        setError(field, {
          type: 'required',
          message: `${getFieldDisplayName(field)} is required`
        });
      });
       
      // Show error toast with all missing fields
      const fieldNames = emptyFields.map(field => getFieldDisplayName(field)).join(', ');
      toast.error(`Please fill in the following required fields: ${fieldNames}`);
      return;
    } else {
      //clearErrors();
    }

    // If all required fields are filled, allow tab change
    setActiveTab(tabId);
  };

  const getFieldDisplayName = (field) => {
    switch(field) {
      case 'name': return 'Customer Name';
      case 'parent_account_id': return 'Parent Account';
      case 'account_number': return 'Account Number';
      case 'primary_contact.first_name': return 'First Name';
      case 'primary_contact.last_name': return 'Last Name';
      case 'primary_contact.prefix_id': return 'Prefix';
      default: return field;
    }
  };

  const getTabFields = (tabId) => {
    switch (tabId) {
      case 'account-info':
        return ['name' , 'primary_contact.first_name', 'primary_contact.last_name','primary_contact.emails','primary_contact.phone_numbers','service_locations'];
      default:
        return [];
    }
  };

  const onSubmit = async (data) => {
    showLoading();
    setIsLoading(true);
    
    try {
      if (id) {
        const response = await updateCustomer(id, data);
        if (response.status === 200) {
          toast.success('Customer updated successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/customers'));
        } else {
          handleErrorResponse(response);
        }
      } else {
        const response = await addCustomer(data);
        if (response.status === 200) {
          toast.success('Customer added successfully!');
          setIsLoading(false);
          navigate(getRoutePath('/customers'));
        } else {
          handleErrorResponse(response);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while saving the customer');
      setIsLoading(false);
    } finally {
      hideLoading();
    }
  };
  
  const handleErrorResponse = (response) => {
    setIsLoading(false);
   
    if (response.errors) {
            
      Object.entries(response.errors).forEach(([field, message]) => {
        // Extract actual message
        let errorMessage = '';

        if (Array.isArray(message)) {
          errorMessage = message[0]; // handle array case
        } else if (typeof message === 'object' && message !== null) {
          errorMessage = Object.values(message)[0]; // take the first value from the object
        } else {
          errorMessage = String(message); // fallback
        }

        setError(field, {
          type: 'manual',
          message: errorMessage,
        });

      
        toast.error(`${field}: ${errorMessage}`);
        
      });
    } else if (response.error?.message) {
      setError('general', {
        type: 'manual',
        message: response.error.message,
      });
    
    }
  };

  const renderTabContent = () => {
    const commonProps = {
      register,
      errors,
      watch,
      setValue,
      clearErrors,
      setError,
      inputClassName: "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
    };
    
    switch (activeTab) {
      case 'account-info':
        return <AccountInfoTab {...commonProps} getEditData={getEditData} getCustomerTags={getCustomerTags} getReferralSources={getReferralSources} getIndustries={getIndustries} getAssignedContracts={getAssignedContracts} getTaxItems={getTaxItems} getCurrencies={getCurrencies} getAssignedAgents={getAssignedAgents} getPrefixes={getPrefixes} getFrequencies={getFrequencies} getCustomers={getCustomers} />;
      case 'financial-data':
        return <FinancialDataTab {...commonProps} paymentMethods={paymentMethods} setPaymentMethods={setPaymentMethods} paymentTerms={paymentTerms} setPaymentTerms={setPaymentTerms}/>;
      case 'equipment':
        return <EquipmentTab {...commonProps} equipmentTypes={equipmentTypes} setEquipmentTypes={setEquipmentTypes} equipmentManufacturers={equipmentManufacturers} setEquipmentManufacturers={setEquipmentManufacturers} paymentAccounts={paymentAccounts} setPaymentAccounts={setPaymentAccounts} />;
      case 'documents':
        return <DocumentsTab {...commonProps} documents={documents} setDocuments={setDocuments} customer={getEditData} />;
      case 'history':
        return <HistoryTab {...commonProps} history={history} customer={getEditData} />;
      case 'logs':
        return <LogsTab {...commonProps} logs={logs} />;
      default:
        return null;
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <Breadcrumb />
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {id ? 'Edit Customer' : 'Create New Customer'}
          </h1>

          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-thin">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-[#009efb] text-[#009efb]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="mt-6">
              {renderTabContent()}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-center sm:justify-between md:justify-end mt-6">
              <div className="flex space-x-4">
                {activeTab !== 'account-info' && (
                  <Link
                    to="#"
                    onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) - 1].id)}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowBigLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Link>
                )}
                {activeTab !== 'logs' ? (
                  <Link
                    to="#"
                    onClick={() => handleTabChange(tabs[tabs.findIndex(t => t.id === activeTab) + 1].id)}
                    className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                  >
                    Next
                    <ArrowBigRight className="w-4 h-4 ml-2" />
                  </Link>
                ) : (
                  isLoading ? (
                    <div className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                    >
                      <SaveIcon className="w-4 h-4 mr-2" />
                      Save
                    </button>
                  )
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerForm; 